/**
 * Review decision + candidate pool. Plan §26.8.
 *
 * Kaiyang Lesson §25.10 / ADR-0002 red line #5: reviewer is an AUDIT field on
 * the immutable decision, NEVER an owner/assignee on the card. `reviewer` is
 * `z.string().email()` so the audit trail is operator-verifiable.
 *
 * ADR-0002 red line #5: ReviewDecision.evidenceRefIds MUST be non-empty.
 * A canonical promotion without evidence is not auditable.
 */

import { z } from "zod";
import { ReviewTransitionState } from "./primitives";

export const ReviewReasonCode = z.enum([
  "source_verified",
  "article_citation_present",
  "awaiting_member_state_confirmation",
  "schema_mismatch",
  "parser_uncertainty",
  "conflict_with_existing_canonical",
  "candidate_duplicate",
  "out_of_scope",
  "other",
]);
export type ReviewReasonCode = z.infer<typeof ReviewReasonCode>;

export const ReviewDecisionSchema = z.object({
  decisionId: z.string().uuid(),
  targetObjectType: z.enum(["rule", "obligation", "evidence", "signal"]),
  targetObjectId: z.string().min(1),
  transitionFrom: ReviewTransitionState,
  transitionTo: ReviewTransitionState,
  reviewer: z.string().email(),
  reasonCodes: z.array(ReviewReasonCode).min(1),
  reasonText: z.string().optional(),
  decidedAt: z.string(),
  evidenceRefIds: z.array(z.string()).min(1),
  snapshotIdBefore: z.string().optional(),
  snapshotIdAfter: z.string().optional(),
});
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>;

export const CandidatePoolEntrySchema = z.object({
  entryId: z.string().uuid(),
  targetObjectKind: z.enum(["rule", "signal", "evidence"]),
  payload: z.record(z.unknown()),
  trustTier: z.enum(["high", "medium", "low"]),
  monitoringStage: z.enum(["new", "in_review", "blocked", "approved"]),
  detectedAt: z.string(),
  sourceBundleId: z.string().optional(),
});
export type CandidatePoolEntry = z.infer<typeof CandidatePoolEntrySchema>;
